import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { ApiError } from '@bitwize/api-client';
import type { PublicService } from '../../services/publicApi';

import { useI18n } from '../../../shared/i18n';
import { publicApiClient } from '../../../shared/lib/publicApiClient';
import { Button, Checkbox, Field, Input, Select, Textarea } from '../../../shared/ui';
import { submitContact, type ContactAccepted } from '../publicApi';

export interface ContactFormProps {
  formToken: string;
  services: readonly PublicService[];
  /** Prefills the service dropdown — a service detail page's CTA links here. */
  defaultServiceSlug?: string | undefined;
}

interface FormValues {
  name: string;
  email: string;
  phone: string;
  subject: string;
  serviceId: string;
  message: string;
  consent: boolean;
  /** The honeypot. A human never sees or fills this field. */
  website: string;
}

const EMPTY: FormValues = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  serviceId: '',
  message: '',
  consent: false,
  website: '',
};

type FieldErrors = Partial<Record<keyof FormValues, string>>;

/**
 * Client-side rules identical to the server's: `name`/`email`/`message` are
 * required and `email` must look like one, `consent` must be checked. The
 * server is still the authority — this only gets a visitor to a valid
 * submission without a round trip for every typo.
 */
function validate(values: FormValues, t: (key: string) => string): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.name.trim()) errors.name = t('forms.validation.required');
  if (!values.email.trim()) errors.email = t('forms.validation.required');
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
    errors.email = t('forms.validation.email');
  if (!values.message.trim()) errors.message = t('forms.validation.required');
  if (!values.consent) errors.consent = t('forms.validation.required');
  return errors;
}

export function ContactForm({ formToken, services, defaultServiceSlug }: ContactFormProps) {
  const { locale, t } = useI18n();
  const formId = useId();
  const firstErrorRef = useRef<HTMLElement | null>(null);
  /** A ref callback usable on any focusable element, for the first invalid field. */
  const focusTarget = (active: boolean) => (el: HTMLElement | null) => {
    if (active) firstErrorRef.current = el;
  };

  const [values, setValues] = useState<FormValues>({
    ...EMPTY,
    serviceId: services.find((s) => s.slug === defaultServiceSlug)?.id ?? '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [accepted, setAccepted] = useState<ContactAccepted | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Focuses the first invalid field once `errors` actually reflects the
   * submission that just failed — not before.
   *
   * This cannot run inline in `onSubmit`: `focusTarget`'s ref callbacks read
   * the *current render's* `errors` prop, and `setErrors` only schedules a
   * re-render, so `firstErrorRef.current` at the point right after calling
   * it still holds whatever the *previous* render left there (`null` on a
   * first failed attempt — nothing focuses at all). An effect keyed on
   * `errors` runs after React has committed that re-render and the ref
   * callbacks have already fired with the new error state, so
   * `firstErrorRef.current` is guaranteed current by the time this reads it.
   */
  useEffect(() => {
    if (Object.keys(errors).length > 0) firstErrorRef.current?.focus();
  }, [errors]);

  function set<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (status === 'submitting') return;

    const fieldErrors = validate(values, t);
    setErrors(fieldErrors);
    // Focus-on-error is handled by the effect above, once `errors` has
    // actually propagated to the fields' refs — see its comment.
    if (Object.keys(fieldErrors).length > 0) return;

    setStatus('submitting');
    setErrorMessage(null);
    try {
      const response = await submitContact(publicApiClient(locale), {
        name: values.name,
        email: values.email,
        message: values.message,
        consent: values.consent,
        locale,
        website: values.website,
        form_token: formToken,
        ...(values.phone ? { phone: values.phone } : {}),
        ...(values.subject ? { subject: values.subject } : {}),
        ...(values.serviceId ? { service_id: values.serviceId } : {}),
      });
      setAccepted(response);
      setStatus('success');
    } catch (error) {
      // Entered content is preserved deliberately — `values` is untouched.
      setStatus('error');
      if (error instanceof ApiError && error.status === 429) {
        setErrorMessage(t('common.contact.errorRateLimited'));
      } else {
        setErrorMessage(t('common.contact.errorGeneric'));
      }
    }
  }

  if (status === 'success' && accepted) {
    return (
      <div className="flex flex-col gap-4 rounded-lg border border-success-border bg-success-bg p-6">
        <div>
          <p className="font-semibold text-success-fg">{t('common.contact.successTitle')}</p>
          <p className="mt-1 text-sm text-success-fg">{t('common.contact.successDescription')}</p>
        </div>
        <dl className="grid grid-cols-1 gap-2 text-sm text-success-fg sm:grid-cols-2">
          <div>
            <dt className="opacity-80">{t('common.contact.name')}</dt>
            <dd>{values.name}</dd>
          </div>
          <div>
            <dt className="opacity-80">{t('common.contact.email')}</dt>
            <dd dir="ltr">{values.email}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="opacity-80">{t('common.contact.message')}</dt>
            <dd className="whitespace-pre-wrap">{values.message}</dd>
          </div>
        </dl>
        <Button
          type="button"
          variant="secondary"
          className="self-start"
          onClick={() => {
            setValues(EMPTY);
            setAccepted(null);
            setStatus('idle');
          }}
        >
          {t('common.contact.sendAnother')}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      {/* Hidden with CSS, not `type="hidden"` — a bot filling every field it
          can see fills this one too; a human never does, because they
          cannot see it either. */}
      <div className="absolute -start-[9999px]" aria-hidden="true">
        <label htmlFor={`${formId}-website`}>Website</label>
        <input
          id={`${formId}-website`}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => set('website', e.target.value)}
        />
      </div>

      <Field label={t('common.contact.name')} required error={errors.name}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            ref={focusTarget(Boolean(errors.name))}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            autoComplete="name"
          />
        )}
      </Field>

      <Field label={t('common.contact.email')} required error={errors.email}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            type="email"
            ref={focusTarget(Boolean(errors.email && !errors.name))}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={values.email}
            onChange={(e) => set('email', e.target.value)}
            autoComplete="email"
          />
        )}
      </Field>

      <Field label={t('common.contact.phone')}>
        {({ id }) => (
          <Input
            id={id}
            type="tel"
            value={values.phone}
            onChange={(e) => set('phone', e.target.value)}
            autoComplete="tel"
          />
        )}
      </Field>

      <Field label={t('common.contact.subject')}>
        {({ id }) => (
          <Input id={id} value={values.subject} onChange={(e) => set('subject', e.target.value)} />
        )}
      </Field>

      {services.length > 0 ? (
        <Field label={t('common.contact.serviceOfInterest')}>
          {({ id }) => (
            <Select
              id={id}
              value={values.serviceId}
              onChange={(e) => set('serviceId', e.target.value)}
            >
              <option value="">{t('common.contact.anyService')}</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.title}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ) : null}

      <Field label={t('common.contact.message')} required error={errors.message}>
        {({ id, describedBy, invalid }) => (
          <Textarea
            id={id}
            ref={focusTarget(Boolean(errors.message && !errors.name && !errors.email))}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            rows={5}
            value={values.message}
            onChange={(e) => set('message', e.target.value)}
          />
        )}
      </Field>

      <div className="flex items-start gap-2.5">
        {/*
          24px rather than the component's default 20: this is the one
          checkbox a visitor on a phone has to hit, and 20px is under the
          WCAG 2.5.8 minimum. Set here rather than on `Checkbox` itself —
          the admin's forms are dense tables of them and are not touch
          surfaces. The label is still associated, so the text toggles it too.
        */}
        <Checkbox
          className="size-6"
          id={`${formId}-consent`}
          checked={values.consent}
          onCheckedChange={(checked) => set('consent', checked === true)}
          aria-describedby={errors.consent ? `${formId}-consent-error` : undefined}
          aria-invalid={Boolean(errors.consent)}
        />
        <label htmlFor={`${formId}-consent`} className="text-sm text-text-muted">
          {t('common.contact.consent')}
        </label>
      </div>
      {errors.consent ? (
        <p id={`${formId}-consent-error`} className="-mt-3 text-sm text-danger">
          {errors.consent}
        </p>
      ) : null}

      {status === 'error' && errorMessage ? (
        <p role="alert" className="text-sm text-danger">
          {errorMessage}
        </p>
      ) : null}

      <Button type="submit" disabled={status === 'submitting'} aria-busy={status === 'submitting'}>
        {status === 'submitting' ? t('common.contact.submitting') : t('common.contact.submit')}
      </Button>
    </form>
  );
}
